import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { ref, set, push, get, query, orderByChild, equalTo } from 'firebase/database';
import { auth } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import SEO from '../components/SEO';

export default function CreateVoiceBot() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [user, setUser] = useState<any>(null);
    const [voiceBots, setVoiceBots] = useState<any[]>([]);
    const [botName, setBotName] = useState('');
    const [welcomeMessage, setWelcomeMessage] = useState('Hello! I am your AI voice assistant. How can I help you today?');
    const [voice, setVoice] = useState<'woman' | 'man'>('woman');

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                const botsRef = ref(db, 'voiceBots');
                const botsQuery = query(botsRef, orderByChild('userId'), equalTo(currentUser.uid));
                const snapshot = await get(botsQuery);
                const botsList: any[] = [];
                snapshot.forEach((child) => {
                    botsList.push({ id: child.key!, ...child.val() });
                });
                setVoiceBots(botsList);
            }
        });
        return unsubscribe;
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!auth.currentUser) {
            alert('Please login first');
            return;
        }

        if (!botName.trim()) {
            alert('Please enter a bot name');
            return;
        }

        setLoading(true);

        try {
            const botRef = push(ref(db, 'voiceBots'));
            const botData = {
                id: botRef.key!,
                name: botName.trim(),
                welcomeMessage,
                voice,
                userId: auth.currentUser.uid,
                createdAt: Date.now(),
                type: 'webrtc'
            };
            await set(botRef, botData);
            alert('🎙️ Voice bot created successfully!');
            navigate('/dashboard');
        } catch (error) {
            console.error(error);
            alert('Error creating voice bot: ' + error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
        navigate('/');
    };

    return (
        <>
            <SEO
                title="Create Voice Bot"
                description="Create an AI voice assistant for your website"
                keywords="voice bot, AI assistant, voice AI"
                type="website"
                noindex
            />

            <div style={{ display: 'flex', minHeight: 'calc(100vh - 64px)', background: '#f5f5f5' }}>
                {/* Sidebar */}
                <div style={{
                    width: sidebarCollapsed ? '80px' : '280px',
                    background: '#1e293b',
                    color: 'white',
                    transition: 'width 0.3s',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        style={{
                            position: 'absolute',
                            right: '-12px',
                            top: '20px',
                            width: '24px',
                            height: '24px',
                            background: '#3b82f6',
                            border: 'none',
                            borderRadius: '12px',
                            color: 'white',
                            cursor: 'pointer',
                            zIndex: 10
                        }}
                    >
                        {sidebarCollapsed ? '→' : '←'}
                    </button>

                    <div style={{ padding: sidebarCollapsed ? '20px 0' : '24px', borderBottom: '1px solid #334155' }}>
                        {!sidebarCollapsed && (
                            <>
                                <h1 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '4px' }}>autopilot Voice</h1>
                                <p style={{ fontSize: '12px', color: '#94a3b8' }}>Voice Bot Platform</p>
                            </>
                        )}
                        {sidebarCollapsed && <div style={{ textAlign: 'center', fontSize: '24px' }}>🎙️</div>}
                    </div>

                    <nav style={{ flex: 1, padding: sidebarCollapsed ? '16px 0' : '24px', overflowY: 'auto' }}>
                        <Link to="/dashboard" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', marginBottom: '4px' }}>
                                <span style={{ fontSize: '20px' }}>📊</span>
                                {!sidebarCollapsed && <span>Dashboard</span>}
                            </div>
                        </Link>
                        <div onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer' }}>
                            <span style={{ fontSize: '20px' }}>🚪</span>
                            {!sidebarCollapsed && <span>Logout</span>}
                        </div>
                    </nav>
                </div>

                {/* Main Content */}
                <div style={{ flex: 1, overflow: 'auto', padding: '32px' }}>
                    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎙️</div>
                            <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>Create Voice Bot</h1>
                            <p style={{ color: '#6b7280' }}>Create an AI voice assistant for your website</p>
                        </div>

                        <form onSubmit={handleSubmit} style={{ background: 'white', borderRadius: '20px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>Bot Name *</label>
                                <input
                                    type="text"
                                    value={botName}
                                    onChange={(e) => setBotName(e.target.value)}
                                    placeholder="e.g., Customer Support Voice Bot"
                                    required
                                    style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '10px' }}
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>Welcome Message</label>
                                <textarea
                                    value={welcomeMessage}
                                    onChange={(e) => setWelcomeMessage(e.target.value)}
                                    rows={3}
                                    style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '10px' }}
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>Voice Type</label>
                                <div style={{ display: 'flex', gap: '20px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input type="radio" value="woman" checked={voice === 'woman'} onChange={(e) => setVoice(e.target.value as 'woman')} />
                                        <span>👩 Woman's Voice</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input type="radio" value="man" checked={voice === 'man'} onChange={(e) => setVoice(e.target.value as 'man')} />
                                        <span>👨 Man's Voice</span>
                                    </label>
                                </div>
                            </div>

                            <div style={{ marginTop: '24px' }}>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    style={{
                                        width: '100%',
                                        padding: '14px',
                                        background: '#059669',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 'bold',
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        opacity: loading ? 0.5 : 1
                                    }}
                                >
                                    {loading ? 'Creating...' : '✨ Create Voice Bot'}
                                </button>
                            </div>
                        </form>

                        <div style={{ marginTop: '24px', padding: '16px', background: '#fef3c7', borderRadius: '12px' }}>
                            <p style={{ fontSize: '13px', color: '#92400e' }}>
                                💡 <strong>Note:</strong> Your voice bot uses your existing Flow Builder conversations. 
                                Add triggers like "sales", "support", "hours" in your Flow Builder for voice responses.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}